import React, { useState } from "react";
import { LuCircleCheck, LuDownload, LuMail, LuMessageCircle } from "react-icons/lu";
import { ShareCard } from "../../../components/patterns/ShareCard";
import { Button } from "../../../components/ui";
import { useApi } from "../../../hooks/useApi";
import { useDownloadRecurso } from "../../../hooks/usePdfDownload";
import { formatDate, t } from "../../../i18n";
import { showSuccess } from "../../../lib/feedback";
import { saveService } from "../../../services/save";
import { SaveResponse } from "../../../types/save.types";
import { EmailComposer, WhatsAppComposer } from "../../sharing";
import { buildAppeal } from "../appeal";
import { AppealWizard } from "../useAppealWizard";

const when = (iso: string) => (iso ? formatDate(iso, { dateStyle: "short", timeStyle: "short" }) : "—");

/** Passo 7: resumo do recurso, PDF (que também salva o recurso no pedido) e envio. */
export const FinishedStep: React.FC<{ w: AppealWizard }> = ({ w }) => {
  const order = w.order!;
  const download = useDownloadRecurso();
  const saveApi = useApi<SaveResponse>();
  const [share, setShare] = useState<"email" | "whatsapp" | null>(null);

  const downloadPdf = async () => {
    const appeal = buildAppeal(w.header, order, w.selectedItems, w.activeGrounds, w.texts, w.selectedRefList);
    await download.download(appeal, `${w.header.id}.pdf`);
    const saved = await saveApi.request(() => saveService.post(saveApi.apiFetch, { timestamp: order.id, recurso: appeal }));
    if (saved?.status === "success") showSuccess("appealSaved");
  };

  const rows: [string, string | number][] = [
    [t("appeals.done.protocol"), w.header.id],
    [t("appeals.done.generatedAt"), when(w.header.timestamp_recurso)],
    [t("appeals.done.validatedAt"), when(w.header.timestamp_validacao)],
    [t("appeals.confirm.patient"), order.paciente],
    [t("appeals.done.references"), w.selectedRefList.length],
  ];

  return (
    <>
      <div className="flex flex-col items-center gap-2 text-center">
        <span className="grid h-16 w-16 animate-scale-in place-items-center rounded-full bg-primary-tint text-primary" aria-hidden>
          <LuCircleCheck size={36} />
        </span>
        <h2 className="text-title-sm font-semibold">{t("appeals.done.title")}</h2>
        <p className="text-body text-fg-muted">{t("appeals.done.body", { procedure: order.procedimento })}</p>
      </div>

      <dl className="grid grid-cols-kv gap-x-3.5 gap-y-2 text-body">
        {rows.map(([label, value]) => (
          <React.Fragment key={label}>
            <dt className="text-fg-muted">{label}</dt>
            <dd className="min-w-0 break-words font-medium">{value}</dd>
          </React.Fragment>
        ))}
      </dl>
      <p className="border-t border-line pt-2.5 text-caption text-fg-subtle">{t("appeals.done.disclaimer")}</p>

      <Button variant="primary" size="lg" loading={download.loading || saveApi.loading} icon={<LuDownload size={18} aria-hidden />} onClick={downloadPdf}>
        {t("appeals.done.download")}
      </Button>
      <div className="grid gap-2.5 sm:grid-cols-2">
        <ShareCard tone="mail" icon={<LuMail size={20} />} title={t("appeals.done.email")} description={t("appeals.done.emailHint")} onClick={() => setShare("email")} />
        <ShareCard
          tone="whatsapp"
          icon={<LuMessageCircle size={20} />}
          title={t("appeals.done.whatsApp")}
          description={t("appeals.done.whatsAppHint")}
          onClick={() => setShare("whatsapp")}
        />
      </div>
      <Button variant="text" className="self-center" onClick={w.reset}>
        {t("appeals.done.restart")}
      </Button>

      {share === "email" && <EmailComposer onClose={() => setShare(null)} patientName={order.paciente} />}
      {share === "whatsapp" && <WhatsAppComposer onClose={() => setShare(null)} patientName={order.paciente} />}
    </>
  );
};
